"use client";

import React, { useEffect, useMemo } from "react";
import type { CreateOptionsType, EmblaOptionsType } from "embla-carousel";
import { DotButton, useDotButton } from "./EmblaCarouselDotButton";
import { usePrevNextButtons } from "./EmblaCarouselArrowButtons";
import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { ChevronLeft, ChevronRight } from "lucide-react";

type Options = {
  playOnInit?: boolean;
  delay?: number;
  draggable?: boolean;
  stopOnInteraction?: boolean;
  stopOnMouseEnter?: boolean;
};

type PropType = {
  className?: string;
  slides: React.ReactElement[];
  options?: EmblaOptionsType;
  userOptions?: Partial<CreateOptionsType<Options>> | undefined;
  arrows?: boolean;
  dots?: boolean;
};

const EmblaCarousel: React.FC<PropType> = (props) => {
  const { className, slides, options, userOptions } = props;

  const autoplayPlugin = useMemo(() => {
    if (userOptions?.playOnInit === false) return null;
    return Autoplay({
      delay: userOptions?.delay ?? 20000,
      stopOnInteraction: userOptions?.stopOnInteraction ?? true,
      stopOnMouseEnter: userOptions?.stopOnMouseEnter ?? true,
    });
  }, [userOptions]);

  const plugins = useMemo(
    () => (autoplayPlugin ? [autoplayPlugin] : []),
    [autoplayPlugin],
  );

  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);

  useEffect(() => {
    if (!emblaApi) return;

    const reinit = () => {
      const current = emblaApi.selectedScrollSnap();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          emblaApi.reInit();
          emblaApi.scrollTo(current, true);
        });
      });
    };

    window.addEventListener("popstate", reinit);
    const rootNode = emblaApi.rootNode();
    const ro = new ResizeObserver(() => reinit());
    ro.observe(rootNode);

    setTimeout(reinit, 0);

    return () => {
      window.removeEventListener("popstate", reinit);
      ro.disconnect();
    };
  }, [emblaApi]);

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  return (
    <section
      className={`${className ?? ""} embla relative w-full m-auto px-2 md:px-0`}
    >
      <div className="embla__viewport overflow-hidden" ref={emblaRef}>
        <div className="embla__container flex">
          {slides.map((content, index) => (
            <div
              className="embla__slide min-w-full flex items-stretch"
              key={index}
            >
              <div className="w-full flex h-full">{content}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="absolute top-1/2 -translate-y-1/2 w-full left-0 flex justify-between pointer-events-none z-10 px-2 md:-mx-8 lg:-mx-16 md:w-[calc(100%+4rem)] lg:w-[calc(100%+8rem)]">
        {props.arrows === true && slides.length > 1 && (
          <>
            <button
              onClick={onPrevButtonClick}
              disabled={prevBtnDisabled}
              className="pointer-events-auto w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand-primary text-white flex items-center justify-center shadow-xl disabled:opacity-30 transition-all hover:scale-110"
              type="button"
            >
              <ChevronLeft size={24} />
            </button>

            <button
              onClick={onNextButtonClick}
              disabled={nextBtnDisabled}
              className="pointer-events-auto w-10 h-10 md:w-12 md:h-12 rounded-full bg-brand-primary text-white flex items-center justify-center shadow-xl disabled:opacity-30 transition-all hover:scale-110"
              type="button"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}
      </div>

      <div className="absolute -bottom-10 w-full">
        {props.dots === true && (
          <div className="flex justify-center items-center gap-2 h-full">
            {scrollSnaps.map((_, index) => (
              <DotButton
                key={index}
                onClick={() => onDotButtonClick(index)}
                className={`embla__dot w-3 h-3 rounded-full transition-all ${
                  index === selectedIndex
                    ? "bg-brand-primary scale-125"
                    : "bg-gray-300"
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default EmblaCarousel;
